/**
 * DashboardLayout.tsx — the app shell.
 * Sidebar: brand, department switcher, workspace nav (with the Sales counts),
 *          the Tools group, the account menu in the user footer.
 * Topbar:  derived breadcrumb (a list) + the per-page primary-action SLOT +
 *          the theme capsule.
 *
 * REDESIGN SLICE 2b (2026-09-27, the shell follow-up; ADOPTION.md §13.5 rules
 * it, §14 records it; register row 62) — the five decisions, all Klemen's:
 *   1. The department switcher stays FLAT (nine rows, mockup-ruled). The
 *      boards show a dropdown; that is a DELIBERATE DEVIATION from the board,
 *      ruled 2026-09-27: the dropdown hides nine items behind a menu the
 *      inventory reads as lost, and the information architecture is not worth
 *      that yet. Recorded as his ruling, not a miss.
 *   2. A TOOLS group in the sidebar: Mission Control (the mode switch), Calendar
 *      (still its coming-soon toast, still named "Calendar (coming soon)") and,
 *      in Sales, Archive (the same page, moved out of Workspace — it stays in
 *      DEPARTMENTS so the active department still derives from it). Mission
 *      Control and Calendar LEFT the bar: same names, same handlers.
 *   3. The user footer is the ACCOUNT MENU: a menu button (aria-haspopup) whose
 *      menu holds Settings and Log out — the same handlers the bar's buttons
 *      had. The theme capsule stays visible in the bar (the frequent control
 *      stays one click).
 *   4. The per-page PRIMARY-ACTION SLOT: `primaryAction` renders in the bar to
 *      the right of the breadcrumb. Nothing passes it yet — each page slice
 *      moves its own button in when that page is re-shot. A page with no
 *      primary action renders NO slot: nothing is invented.
 *   5. The search capsule and the ⌘K listener LEFT dashboard mode (App.tsx
 *      keeps ⌘K for Mission Control, whose palette exists). Honest search over
 *      contacts, deals, accounts and leads is its own register row (63), OPEN;
 *      until it lands the boards show a search field the product does not
 *      have — a DELIBERATE DEVIATION, Klemen's, not a miss.
 *   Also here: the breadcrumb is a LIST (nav > ol > li), so assistive
 *   technology separates "Sales" from "Dashboard" — the chevron between them
 *   is decorative and aria-hidden, and as bare spans the two ran together
 *   (found 2026-09-27 on the served page by the §36.13 proof).
 *
 * REDESIGN SLICE 2 (2026-09-26, the calm-minimal shell; ADOPTION.md §13,
 * register row 49) — what changed and what deliberately did not:
 *   · The 34px page title in the bar became the boards' BREADCRUMB
 *     ("Sales › Pipeline": department in the secondary ink, page at 600),
 *     derived from DEPARTMENTS + activePage — no router, no link, no new
 *     affordance. The in-page 34px title is each page slice's job.
 *   · The brand block is the boards' 28px "K" square + "Klaxiora"; the
 *     department overline under it went (the switcher shows the department).
 *   · Group labels read sentence-case ("Departments", "Workspace"); rows are
 *     40px / 14px with 18px icons on the boards' sidebar ground.
 *   · The Sales rows carry REAL counts from /v1/sales/stats — Pipeline the
 *     FENCED open-deal count with the F-1d "(+k excluded)" suffix, Contacts
 *     the true total — fetched once per Sales page mount, rendered only when
 *     the read succeeds (the slot rule: no number, no chip).
 *   · The inert Dashboard tab of the shell switcher is GONE (ruled: no
 *     handler, no such control in the spec); Mission Control is the one
 *     button it leaves. The theme capsule, Calendar, Settings and Log out
 *     stayed in the bar at 36px / rounded-10 until slice 2b (above) moved all
 *     but the theme capsule — a live affordance was not dropped for a picture.
 *   · The five items that awaited a ruling here (the dropdown department
 *     switcher, the Tools group, the user kebab menu, a per-page
 *     primary-action slot, the search capsule + ⌘K) were RULED 2026-09-27 —
 *     the SLICE 2b block above is the record; this bullet is history. Main's
 *     padding is untouched: page padding is per page.
 *
 * S-TIER FOUNDATION (slice 1): restyled onto the .k-* component contract in
 * src/styles/klaxiora.css — 236px sidebar, mono group labels, .k-navrow rows,
 * .k-search capsule with its ⌘K chip, .k-segmented controls, .k-topbar and the
 * .k-page padding. This is a RESTYLE IN PLACE: the information architecture is
 * untouched. DEPARTMENTS, NAV_ICONS, PAGE_TITLES, navModel, NAV_HIDDEN and
 * SHOW_PLATFORM_SECTION are all exactly as they were — this slice changes how
 * the shell looks, never what it contains or where it goes.
 *
 * Two things are genuinely new, both required by the system:
 *   · The THEME CAPSULE (dark/light), backed by theme/theme.ts.
 *   · The count-chip SLOT on nav rows (.k-count). It is a slot and nothing
 *     more: no nav item has an honest per-item count wired yet, so nothing
 *     renders anywhere today. Each department slice wires its own real number.
 *     Rendering a fabricated count to match a reference screen would break the
 *     data-honesty invariant, which outranks the visual authority (CLAUDE.md).
 *
 * Colour/size values still flow from src/theme/tokens.ts, which now re-exports
 * the design system's custom properties — so this shell themes for free.
 */

import {
  Settings, Calendar, Zap, LogOut, ChevronRight, EllipsisVertical, UserRound,
  LayoutGrid, Inbox as InboxIcon, Columns3, Users, ChartColumn, Activity,
  Archive, FileText, Send, CircleCheck, CircleX, Wallet, Target, GitBranch,
  Briefcase, UserPlus, Ticket, Banknote, GraduationCap, FileSearch, Bot, Building2,
  CalendarClock, Linkedin, Megaphone, TrendingUp, Mail, ScrollText, Scale,
  Gavel, History, BookOpen, ClipboardCheck, AlertTriangle, Package,
  CalendarDays, Moon, Sun, Network, type LucideIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { useCallback, useEffect, useRef, useState } from 'react'
import { accent, edge, layout, surface, track } from '../../theme/tokens'
import { setTheme, useTheme } from '../../theme/theme'
import { activeDepartment } from './navModel'
import { fetchSalesReports, fetchSalesStats } from './api'
import { excludedSuffix } from './pages/sales/fenceCopy'

// ── Department nav structure ──────────────────────────────────────────────────

// feature: when set, the item renders only if the tenant holds that enabled
// feature key (GET /v1/me features). Cosmetic — the server 403 is the boundary.
// The .k-count chip is wired per row from a read path (useSalesNavCounts);
// it renders ONLY when a real number arrived, never a placeholder or a zero
// standing in for "unknown" (data honesty outranks the reference screens).
interface NavItem  { id: string; label: string; feature?: string }
interface Department {
  id: string
  label: string
  items: NavItem[]
}

// ── Sidebar visibility flags (§14.D) — HIDDEN, never deleted. Flip a flag to
// restore. Hiding 'inbox' removes the sidebar path to the LIVE triage queue
// (inbox.triage); hiding the Platform section removes System Health / Run
// Analytics / Model Usage from the shell. Routes, PAGE_TITLES entries and
// page components all remain — the pages are unlinked, not gone.
const NAV_HIDDEN: Record<string, boolean> = { inbox: true }
const SHOW_PLATFORM_SECTION = false

const DEPARTMENTS: Department[] = [
  {
    // Tenant-level, department-agnostic: the triage queue reviews inbound
    // emails BEFORE they belong to any department — hence its own section.
    id: 'inbox', label: 'Inbox',
    items: [
      { id: 'inbox.triage', label: 'Triage' },
    ],
  },
  {
    id: 'sales', label: 'Sales',
    items: [
      { id: 'sales.dashboard',  label: 'Dashboard' },
      // L8-4 LOCKED (Klemen): Inbound Leads sits SECOND, immediately after
      // Dashboard — top-of-funnel, per the full handoff tab order.
      { id: 'sales.leads',      label: 'Inbound Leads' },
      { id: 'sales.pipeline',   label: 'Pipeline' },
      { id: 'sales.contacts',   label: 'Contacts' },
      // Slice A (Phase 1 / 1.1): Accounts sits after Contacts — the entity
      // the contacts and deals link to. Every sales baseline moved one row.
      { id: 'sales.accounts',   label: 'Accounts' },
      { id: 'sales.reports',    label: 'Reports' }, // mockup nav order (R6-6): after Contacts
      { id: 'sales.activities', label: 'Activities' },
      { id: 'sales.archive',    label: 'Archive' },
    ],
  },
  {
    id: 'finance', label: 'Finance',
    items: [
      { id: 'finance.overview',  label: 'Overview' },
      { id: 'finance.invoices',  label: 'Invoices' },
      { id: 'finance.issued-invoices', label: 'Issued Invoices' },
      { id: 'finance.approvals', label: 'Approvals' },
      { id: 'finance.rejections', label: 'Rejections' },
      { id: 'finance.expenses',  label: 'Expenses' },
      { id: 'finance.budgets',   label: 'Budgets' },
      { id: 'finance.reports',   label: 'Reports' },
    ],
  },
  {
    id: 'hr', label: 'HR',
    items: [
      { id: 'hr.overview',    label: 'Overview' },
      { id: 'hr.employees',   label: 'Employees' },
      { id: 'hr.lifecycle',   label: 'Lifecycle' },
      { id: 'hr.job-postings', label: 'Job Postings' },
      { id: 'hr.onboarding',  label: 'Onboarding' },
      // Org Chart sits before Tickets — the mockup's relative order (:3395).
      { id: 'hr.org-chart',   label: 'Org Chart' },
      { id: 'hr.tickets',     label: 'Tickets' },
    ],
  },
  {
    // Directly after HR, deliberately: payroll computes over HR's employee
    // records (profiles feed the ledger), so adjacency mirrors the data flow.
    // A standalone section, not an HR item — the pay-run view is a run across
    // employees, not a property of one.
    id: 'payroll', label: 'Payroll',
    items: [
      { id: 'payroll.pay-run', label: 'Pay Run' },
    ],
  },
  {
    id: 'recruitment', label: 'Recruitment',
    items: [
      { id: 'recruitment.overview',  label: 'Overview' },
      { id: 'recruitment.jobs',      label: 'Job Postings' },
      { id: 'recruitment.candidates',label: 'Candidates' },
      { id: 'recruitment.providers', label: 'Providers', feature: 'learning_providers' },
      { id: 'recruitment.cv',        label: 'CV Screening' },
    ],
  },
  {
    id: 'operations', label: 'Operations',
    items: [
      { id: 'operations.overview',  label: 'Overview' },
      { id: 'operations.agents',    label: 'Agents' },
      { id: 'operations.runs',      label: 'Active Runs' },
      { id: 'operations.scheduled', label: 'Schedules' },
      // M5a: Field Schedule — the mockup's Schedules tab slot (:3421,
      // between Active Runs and SOPs). Named apart from the cron
      // "Schedules" row above, which is agent-ops and untouched (the §B.4
      // name-collision rule: the field entity is tasks, not schedules).
      { id: 'operations.tasks',     label: 'Field Schedule' },
      // M1: SOPs — first real field-ops page; sits after Schedules per the
      // mockup's Operations nav order (klaxiora-crm-mockup-full.html :3422).
      { id: 'operations.sops',      label: 'SOPs' },
      // M3: Incidents — between SOPs and Quality Checks per the mockup tab
      // order (:3422-3425: sops, incidents, assets, quality).
      { id: 'operations.incidents', label: 'Incidents' },
      // M4: Assets — between Incidents and Quality Checks per the mockup tab
      // order (:3422-3425: sops, incidents, assets, quality).
      { id: 'operations.assets',    label: 'Assets' },
      // M2: Quality Checks — last mockup tab (:3425); LinkedIn stays last
      // as the live-only page.
      { id: 'operations.quality',   label: 'Quality Checks' },
      { id: 'operations.linkedin',  label: 'LinkedIn' },
    ],
  },
  {
    id: 'marketing', label: 'Marketing',
    items: [
      { id: 'marketing.overview',   label: 'Overview' },
      { id: 'marketing.campaigns',  label: 'Campaigns' },
      { id: 'marketing.analytics',  label: 'Analytics' },
      { id: 'marketing.email',      label: 'Email' },
    ],
  },
  {
    id: 'legal', label: 'Legal',
    items: [
      { id: 'legal.overview',   label: 'Overview' },
      { id: 'legal.contracts',  label: 'Contracts' },
      { id: 'legal.compliance', label: 'Compliance' },
    ],
  },
  {
    id: 'bids', label: 'Bids',
    items: [
      { id: 'bids.overview',   label: 'Overview' },
      { id: 'bids.active',     label: 'Active Bids' },
      { id: 'bids.history',    label: 'History' },
    ],
  },
]

// ── Workspace item icons (S14-2) — the mockup's nav is icon+label
// (klaxiora-crm-mockup-full.html :3351+, 17px .nav-item .icon). Keyed by
// route id; lucide equivalents of the mockup's ICON_* where the page exists
// there, in-spirit picks for live-only pages. Departments cards stay
// icon-less (the mockup's are).
const NAV_ICONS: Record<string, LucideIcon> = {
  'overview.home':          LayoutGrid,
  'sales.dashboard':        LayoutGrid,
  'sales.leads':            InboxIcon,
  'sales.pipeline':         Columns3,
  'sales.contacts':         Users,
  'sales.accounts':         Building2,
  'sales.reports':          ChartColumn,
  'sales.activities':       Activity,
  'sales.archive':          Archive,
  'finance.overview':       LayoutGrid,
  'finance.invoices':       FileText,
  'finance.issued-invoices': Send,
  'finance.approvals':      CircleCheck,
  'finance.rejections':     CircleX,
  'finance.expenses':       Wallet,
  'finance.budgets':        Target,
  'finance.reports':        ChartColumn,
  'hr.overview':            LayoutGrid,
  'hr.employees':           Users,
  'hr.lifecycle':           GitBranch,
  'hr.job-postings':        Briefcase,
  'hr.onboarding':          UserPlus,
  'hr.org-chart':           Network,
  'hr.tickets':             Ticket,
  'payroll.pay-run':        Banknote,
  'recruitment.overview':   LayoutGrid,
  'recruitment.jobs':       Briefcase,
  'recruitment.candidates': Users,
  'recruitment.providers':  GraduationCap,
  'recruitment.cv':         FileSearch,
  'operations.overview':    LayoutGrid,
  'operations.agents':      Bot,
  'operations.runs':        Activity,
  'operations.scheduled':   CalendarClock,
  'operations.tasks':       CalendarDays,
  'operations.sops':        BookOpen,
  'operations.incidents':   AlertTriangle,
  'operations.assets':      Package,
  'operations.quality':     ClipboardCheck,
  'operations.linkedin':    Linkedin,
  'marketing.overview':     LayoutGrid,
  'marketing.campaigns':    Megaphone,
  'marketing.analytics':    TrendingUp,
  'marketing.email':        Mail,
  'legal.overview':         LayoutGrid,
  'legal.contracts':        ScrollText,
  'legal.compliance':       Scale,
  'bids.overview':          LayoutGrid,
  'bids.active':            Gavel,
  'bids.history':           History,
}

// Page title map
const PAGE_TITLES: Record<string, string> = {
  'overview.home':          'Dashboard',
  'inbox.triage':           'Triage',
  'overview.health':        'System Health',
  'automation.agents':      'All Agents',
  'automation.runs':        'Active Runs',
  'automation.scheduled':   'Scheduled Jobs',
  'scraping.linkedin':      'LinkedIn',
  'analytics.performance':  'Run Performance',
  'analytics.models':       'Model Usage',
  'analytics.feedback':     'Feedback Data',
  'sales.dashboard':        'Sales Dashboard',
  'sales.leads':            'Inbound Leads',
  'sales.pipeline':         'Pipeline',
  'sales.contacts':         'Contacts',
  'sales.accounts':         'Accounts',
  'sales.reports':          'Reports',
  'sales.activities':       'Activities',
  'sales.archive':          'Archive',
  'operations.agents':      'Agents',
  'operations.runs':        'Active Runs',
  'operations.scheduled':   'Schedules',
  'operations.tasks':       'Field Schedule',
  'operations.sops':        'SOPs',
  'operations.incidents':   'Incidents',
  'operations.assets':      'Assets',
  'operations.quality':     'Quality Checks',
  'operations.linkedin':    'LinkedIn',
  'settings.company':       'Settings',
  'finance.issued-invoices': 'Issued Invoices',
  'recruitment.providers':  'Learning Providers',
  'payroll.pay-run':        'Pay Run',
  'hr.org-chart':           'Org Chart',
}

// ── One sidebar row ───────────────────────────────────────────────────────────
// `current` drives .k-navrow[aria-current="page"] — the system keys its active
// state on a real accessibility attribute, not a class.
//
// `count` is the .k-count chip SLOT and is the data-honesty boundary: the chip
// renders only when a real number arrives, so today nothing renders anywhere.
// Each department slice wires its own count from its own read path.
function NavRow({ id, label, Icon, current, count, trailing, onNavigate, onActivate, ariaLabel, title, kind = 'page' }: {
  id: string
  label: string
  Icon?: LucideIcon
  current: boolean
  /** A REAL number from a read path, with the rows a fence dropped beside it
   *  (F-1d). Absent → no chip. */
  count?: NavCount
  trailing?: React.ReactNode
  onNavigate: (page: string) => void
  /** Slice 2b: a TOOL row runs this instead of navigating (Mission Control
   *  switches mode, Calendar toasts). A page row leaves it undefined. */
  onActivate?: () => void
  /** Slice 2b: an explicit accessible name where the visible label is shorter
   *  than the control's meaning ("Calendar (coming soon)" — the name the bar's
   *  button had, kept so the inventory sees the same affordance). */
  ariaLabel?: string
  title?: string
  /** Slice 2b: `data-kind` — `page` rows navigate and are what the inventory
   *  walks; `tool` rows act in place. */
  kind?: 'page' | 'tool'
}) {
  return (
    <button
      type="button"
      onClick={() => (onActivate ? onActivate() : onNavigate(id))}
      aria-current={current ? 'page' : undefined}
      aria-label={ariaLabel}
      className="k-navrow w-full text-left cursor-pointer bg-transparent border-0"
      title={title ?? count?.title}
      data-kind={kind}
    >
      <span className="flex items-center gap-[12px] min-w-0">
        {Icon && <Icon size={18} className="flex-shrink-0" />}
        <span className="k-truncate">{label}</span>
      </span>
      {/* The chip is aria-hidden so the row's accessible NAME stays its label
          ("Pipeline", not "Pipeline 9 (+1 excluded)"): a count changes daily
          and must not rename the control (the inventory keys on the name);
          the button's title carries the full sentence instead. */}
      {count ? <span className="k-count" aria-hidden="true">{navCountText(count)}</span> : trailing}
    </button>
  )
}

// ── Nav counts (slice 2) ──────────────────────────────────────────────────────
// The boards show a count beside Pipeline. The honest numbers for the Sales
// rows: /v1/sales/reports' openDealCount — the FENCED open-deal count with
// openExcludedNonGbp beside it (§26 F-1), the SAME figure the Dashboard's
// "Open deals" card shows, so the chip and the card can never disagree
// (/stats' totalDeals is all stages, closed included — measured 16 vs 9 on
// Fourth Coffee, so it is NOT the chip) — and /v1/sales/stats' contacts.total,
// a true COUNT. Two reads per Sales page mount (the Dashboard already makes
// both; lifting them to one top-level read is a later economy); a failed read
// renders nothing — the chip is a slot, never a placeholder.
export interface NavCount { value: number; excluded?: number; title?: string }
export function navCountText(c: NavCount): string {
  const tail = excludedSuffix(c.excluded)
  return tail ? `${c.value} ${tail}` : String(c.value)
}
function useSalesNavCounts(active: boolean, activePage: string): Record<string, NavCount> {
  const [counts, setCounts] = useState<Record<string, NavCount>>({})
  useEffect(() => {
    if (!active) { setCounts({}); return }
    let cancelled = false
    Promise.all([fetchSalesReports(), fetchSalesStats()]).then(([r, s]) => {
      if (cancelled) return
      const excluded = r.openExcludedNonGbp ?? 0
      setCounts({
        'sales.pipeline': { value: r.openDealCount, excluded, title: excluded > 0
          ? `${r.openDealCount} open deals in GBP; ${excluded} excluded — uncaptured non-GBP`
          : `${r.openDealCount} open deals` },
        'sales.contacts': { value: s.contacts.total, title: `${s.contacts.total} contacts` },
      })
    }).catch(() => { if (!cancelled) setCounts({}) })
    return () => { cancelled = true }
  }, [active, activePage])
  return counts
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface DashboardLayoutProps {
  activePage: string
  onNavigate: (page: string) => void
  children: React.ReactNode
  onSwitchMode: () => void
  /** Clears the stored token and returns to the login page. Omitted in stub mode. */
  onLogout?: () => void
  /**
   * Slice 2b, the PRIMARY-ACTION SLOT (ADOPTION.md §13.5 (4)): a page's one
   * primary action ("+ New Deal", "+ New Contact"), rendered in the bar to the
   * right of the breadcrumb. Absent → no slot element at all, so a page with
   * no primary action invents nothing. Nothing passes it yet: each page slice
   * moves its own button in when that page is re-shot.
   */
  primaryAction?: React.ReactNode
  /** The backend-resolved tenant name (GET /v1/me). null until loaded — nothing renders. */
  tenantName?: string | null
  /** The session user's display name (GET /v1/me). Unset for WorkOS users with no name — email is the fallback. */
  userDisplayName?: string | null
  /** The session user's email (GET /v1/me). Absent for dev-token operators — no name line renders. */
  userEmail?: string | null
  /**
   * Feature-entitlement check for feature-tagged nav items (GET /v1/me
   * features). FAIL CLOSED: omitted, or /me not yet loaded, hides every
   * tagged item — the server-side 403 stays the real boundary.
   */
  hasFeature?: (key: string) => boolean
  /**
   * The scoped scroll-model variant for viewport-owning screens (S-Tier slice
   * 3 / ADOPTION.md §6.5: the pipeline board). The .dash-shell grid already
   * bounds the viewport; what this flips is the MAIN region: normally it is
   * the page scroller (overflow-y-auto + .k-page padding), but a
   * viewport-owning page brings its own nested scrollers (.k-board /
   * .k-lane), so main stops scrolling and stops padding — the page fills it
   * exactly. Dashboard.tsx sets this per RENDERED page (not per route id:
   * sales.pipeline renders the normally-scrolling Deal Detail while a deal is
   * drilled). Every other screen keeps the scrolling default.
   */
  viewportOwning?: boolean
}

// Pages the boards list under TOOLS rather than Workspace (slice 2b (2)).
// They stay in DEPARTMENTS so the active department still derives from them.
const TOOL_PAGES = new Set(['sales.archive'])

// ── The account menu (slice 2b, RULED 2026-09-27 (3)) ──────────────────────
// The user footer is a MENU BUTTON. Its visible content is the avatar, the
// session user and the active tenant; its accessible name is the stable
// "Account menu" so the inventory keys one control across sessions (the
// user's name would rename it per login). The menu holds the two controls the
// bar carried — Settings and Log out — with the same handlers. Keyboard:
// Enter / Space open (a button's click); ArrowDown / ArrowUp on the button
// open and focus the first item; ArrowDown / ArrowUp / Home / End move;
// Escape closes and returns focus to the button; Tab closes; an outside
// mousedown closes. Log out renders only when a handler exists, exactly as
// the bar's button did.
function AccountMenu({ userInitials, userLabel, tenantName, onNavigate, onLogout }: {
  userInitials: string | null
  userLabel: string | null
  tenantName?: string | null
  onNavigate: (page: string) => void
  onLogout?: () => void
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const close = useCallback((refocus: boolean) => {
    setOpen(false)
    if (refocus) triggerRef.current?.focus()
  }, [])
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (menuRef.current?.contains(t) || triggerRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); close(true) } }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open, close])
  useEffect(() => {
    if (!open || !menuRef.current) return
    const first = menuRef.current.querySelector<HTMLButtonElement>('[role="menuitem"]')
    if (first && !menuRef.current.contains(document.activeElement)) first.focus()
  }, [open])
  const items = () => Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])
  const onTriggerKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setOpen(true) }
  }
  const onMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const list = items()
    if (list.length === 0) return
    const i = list.indexOf(document.activeElement as HTMLButtonElement)
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      list[e.key === 'ArrowDown' ? (i + 1) % list.length : (i - 1 + list.length) % list.length].focus()
    } else if (e.key === 'Home') { e.preventDefault(); list[0].focus() }
    else if (e.key === 'End') { e.preventDefault(); list[list.length - 1].focus() }
    else if (e.key === 'Tab') { setOpen(false) }
  }
  const pick = (fn: () => void) => () => { close(true); fn() }
  const title = [userLabel, tenantName].filter(Boolean).join(' · ') || 'Account'
  return (
    <div className="mt-auto pt-[14px] relative flex-shrink-0">
      {open && (
        <div ref={menuRef} id="k-account-menu" role="menu" aria-label="Account menu" className="k-menu" onKeyDown={onMenuKey}>
          <button type="button" role="menuitem" className="k-menuitem" onClick={pick(() => onNavigate('settings.company'))}>
            <Settings size={16} className="flex-shrink-0" /> Settings
          </button>
          {onLogout && (
            <button type="button" role="menuitem" className="k-menuitem k-menuitem--danger" onClick={pick(onLogout)}>
              <LogOut size={16} className="flex-shrink-0" /> Log out
            </button>
          )}
        </div>
      )}
      <button
        ref={triggerRef}
        type="button"
        className="k-user"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? 'k-account-menu' : undefined}
        aria-label="Account menu"
        title={title}
        onClick={() => setOpen(o => !o)}
        onKeyDown={onTriggerKey}
      >
        <span className="w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: track.DEFAULT }} aria-hidden="true">
          {userInitials
            ? <span className="text-13 font-semibold leading-none text-ink-primary">{userInitials}</span>
            : <UserRound size={16} className="text-ink-secondary" />}
        </span>
        <span className="flex-1 min-w-0">
          {userLabel && (
            <span className="block text-14 font-semibold text-ink-primary leading-tight truncate">{userLabel}</span>
          )}
          {/* Active tenant — the session scope the backend actually resolved (/v1/me). */}
          {tenantName && (
            <span className="block text-12 text-ink-secondary truncate" title="Active tenant">{tenantName}</span>
          )}
        </span>
        <EllipsisVertical size={16} className="flex-shrink-0 text-ink-secondary" aria-hidden="true" />
      </button>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

export function DashboardLayout({ activePage, onNavigate, children, onSwitchMode, onLogout, primaryAction, tenantName, userDisplayName, userEmail, hasFeature, viewportOwning = false }: DashboardLayoutProps) {
  const theme = useTheme()

  // Feature-tagged nav items render only for entitled tenants (fail closed:
  // no hasFeature or no /me yet → tagged items hidden). Untagged items are
  // unaffected; a department whose items all hide collapses away entirely.
  const visibleDepartments = DEPARTMENTS
    .filter(dept => !NAV_HIDDEN[dept.id])
    .map(dept => ({ ...dept, items: dept.items.filter(item => !item.feature || (hasFeature?.(item.feature) ?? false)) }))
    .filter(dept => dept.items.length > 0)
  // Derived, never stored (§14.C): the active department is a pure function
  // of the route, so the card highlight and the Workspace list cannot desync.
  const activeDept = activeDepartment(visibleDepartments, activePage)

  const navCounts = useSalesNavCounts(activeDept?.id === 'sales', activePage)
  const userLabel = userDisplayName || userEmail || null
  const userInitials = userDisplayName
    ? userDisplayName.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : userEmail
      ? userEmail.split('@')[0].slice(0, 2).toUpperCase()
      : null

  // The breadcrumb: the department's sidebar label › the row's sidebar label;
  // a department-less page (the landing Dashboard, Settings) is one crumb,
  // from PAGE_TITLES. Derived every render — it cannot desync from the rows.
  const pageLabel = activeDept?.items.find(i => i.id === activePage)?.label
    ?? PAGE_TITLES[activePage]
    ?? (() => {
      const parts = activePage.split('.')
      return parts[parts.length - 1].replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())
    })()

  return (
    <div className="dash-shell">
      {/* ── Sidebar — .k-sidebar treatment (18px 12px 10px, --panel, hairline);
             its column is layout['sidebar-w'] (260px since the calm-minimal
             re-land, ADOPTION.md §12) via the .dash-shell grid. ── */}
      <aside
        className="row-start-1 row-end-3 col-start-1 col-end-2 flex flex-col overflow-hidden pt-5 px-[14px] pb-[14px]"
        style={{
          background: surface.sidebar,
          borderRight: `1px solid ${edge.sidebar}`,
          gridArea: 'sidebar',
        }}
      >
        {/* Brand block — the boards' 28px rounded-8 "K" square and the name
            (16px / 600). The square is the primary ink with the panel ink for
            the letter, so it reads on both grounds without a brand token. */}
        <div className="px-2 mb-[22px] flex items-center gap-[10px] flex-shrink-0">
          <span className="w-7 h-7 rounded-[8px] flex items-center justify-center text-14 font-bold flex-shrink-0"
            style={{ background: 'var(--text)', color: 'var(--panel)' }} aria-hidden="true">K</span>
          <span className="text-16 font-semibold text-ink-primary tracking-[-0.01em]">Klaxiora</span>
        </div>

        {/* Nav */}
        <nav aria-label="Primary navigation" className="flex-1 overflow-y-auto flex flex-col gap-[2px]" style={{ scrollbarWidth: 'none' }}>
          {/* Pinned Dashboard (S14-1 option 3) — the one accepted deviation
              from the mockup's strict two-section model: keeps the O-1
              landing (overview.home) one click away. */}
          <NavRow
            id="overview.home"
            label="Dashboard"
            Icon={LayoutGrid}
            current={activePage === 'overview.home'}
            onNavigate={onNavigate}
          />

          {/* DEPARTMENTS — the mockup's flat switcher (:3464-3481): every
              department always visible, no expansion; clicking one lands on
              its FIRST page (switchDepartment semantics, :3495-3501).
              The active department carries aria-current="true" ("current item
              in a set"), NOT "page" — exactly one row in the sidebar may claim
              to be the page, and that is the workspace row below. Both wear
              the same .k-navrow active treatment. */}
          <div className="k-navgroup-label mt-5">Departments</div>
          <div role="group" aria-label="Department switcher" className="flex flex-col gap-[2px]">
            {visibleDepartments.map(dept => {
              const current = activeDept?.id === dept.id
              return (
                <button
                  key={dept.id}
                  onClick={() => onNavigate(dept.items[0].id)}
                  aria-current={current ? 'true' : undefined}
                  className="k-navrow w-full text-left cursor-pointer bg-transparent border-0"
                >
                  <span className="k-truncate">{dept.label}</span>
                  {current && <span className="k-dot" aria-hidden="true" />}
                </button>
              )
            })}
          </div>

          {/* WORKSPACE — only the active department's pages (renderNav,
              :3483-3493), icon+label. Hidden on dept-less routes
              (overview.home / settings) — nothing honest to show. */}
          {activeDept && (
            <div role="group" aria-label="Workspace" className="mt-5 flex flex-col gap-[2px]">
              <div className="k-navgroup-label">Workspace</div>
              {activeDept.items.filter(item => !TOOL_PAGES.has(item.id)).map(item => (
                <NavRow
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  Icon={NAV_ICONS[item.id] ?? LayoutGrid}
                  current={activePage === item.id}
                  count={navCounts[item.id]}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          )}

          {/* TOOLS (slice 2b, RULED 2026-09-27 (2)): the boards' third group.
              Mission Control and Calendar moved here from the bar — same
              accessible names, same handlers (the inventory reads them as the
              same two affordances); Archive is the department's own page
              rendered under Tools instead of Workspace, so it appears only
              while its department is active. */}
          <div role="group" aria-label="Tools" className="mt-5 flex flex-col gap-[2px]">
            <div className="k-navgroup-label">Tools</div>
            <NavRow
              id="tool.mission-control"
              label="Mission Control"
              Icon={Zap}
              current={false}
              kind="tool"
              title="Open Mission Control"
              onActivate={onSwitchMode}
              onNavigate={onNavigate}
            />
            <NavRow
              id="tool.calendar"
              label="Calendar"
              Icon={Calendar}
              current={false}
              kind="tool"
              ariaLabel="Calendar (coming soon)"
              title="Calendar (coming soon)"
              onActivate={() => toast.info('Calendar coming soon')}
              onNavigate={onNavigate}
            />
            {activeDept?.items.filter(item => TOOL_PAGES.has(item.id)).map(item => (
              <NavRow
                key={item.id}
                id={item.id}
                label={item.label}
                Icon={NAV_ICONS[item.id] ?? LayoutGrid}
                current={activePage === item.id}
                count={navCounts[item.id]}
                onNavigate={onNavigate}
              />
            ))}
          </div>

          {/* Platform section — flag-hidden (§14.D), not deleted. */}
          {SHOW_PLATFORM_SECTION && (
            <div className="mt-5 flex flex-col gap-[2px]">
              <div className="k-navgroup-label">Platform</div>
              {[
                { id: 'overview.health',      label: 'System Health',
                  trailing: <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: accent['health-dot'] }} /> },
                // M7b: operations.agents now renders the field-team page, so
                // the AI-agent AgentsList lives on at its dormant
                // automation.agents alias — this row is its only sidebar path.
                { id: 'automation.agents',    label: 'AI Agents' },
                { id: 'analytics.performance',label: 'Run Analytics' },
                { id: 'analytics.models',     label: 'Model Usage' },
              ].map(item => (
                <NavRow
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  current={activePage === item.id}
                  trailing={item.trailing}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          )}
        </nav>

        {/* Pinned user footer — the session user (/v1/me): display name, else
            email; a generic avatar for dev-token operators. Slice 2b: the
            footer IS the account menu's button (Settings, Log out). */}
        <AccountMenu userInitials={userInitials} userLabel={userLabel} tenantName={tenantName} onNavigate={onNavigate} onLogout={onLogout} />
      </aside>

      {/* ── Topbar — .k-topbar treatment (0 40px, the translucent toolbar ground,
             its own hairline); its height is layout['topbar-h'] (64px), the
             same value the .dash-shell grid row reads. The bar carries the
             BREADCRUMB, not the page title (slice 2). ── */}
      <header
        className="flex items-center gap-3 px-10"
        style={{
          gridArea: 'header',
          background: surface.header,
          borderBottom: `1px solid ${edge.topbar}`,
          height: layout['topbar-h'],
        }}
      >
        {/* The breadcrumb is a LIST (slice 2b): each crumb its own item, so a
            screen reader separates "Sales" from "Dashboard"; the chevron is
            decorative. Text, not links — no router (row 54). */}
        <nav aria-label="Breadcrumb" className="flex-1 min-w-0">
          <ol className="k-breadcrumb">
            {activeDept && (
              <li><span className="k-breadcrumb-dept">{activeDept.label}</span></li>
            )}
            <li>
              {activeDept && <ChevronRight size={12} className="flex-shrink-0 text-ink-disabled" aria-hidden="true" />}
              <span className="k-breadcrumb-page" aria-current="page">{pageLabel}</span>
            </li>
          </ol>
        </nav>

        {/* The primary-action SLOT (slice 2b (4)): rendered only when a page
            passes one. No page does yet. */}
        {primaryAction && (
          <div className="flex-shrink-0 flex items-center" data-slot="primary-action">{primaryAction}</div>
        )}

        {/* No search capsule and no ⌘K here since slice 2b (RULED 2026-09-27
            (d)): the control was dead on every page; honest search is row 63. */}

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Theme capsule — gss.theme; LIGHT default since 2026-09-26, the stored
              choice wins (theme/theme.ts). The one bar control slice 2b kept. */}
          <div className="k-segmented" role="tablist" aria-label="Colour theme" style={{ borderRadius: 'var(--r-nav)' }}>
            <button
              role="tab"
              aria-selected={theme === 'dark'}
              onClick={() => setTheme('dark')}
              className="flex items-center cursor-pointer bg-transparent border-0"
              style={{ paddingLeft: '10px', paddingRight: '10px', borderRadius: '8px' }}
              aria-label="Dark theme"
              title="Dark theme"
            >
              <Moon size={13} />
            </button>
            <button
              role="tab"
              aria-selected={theme === 'light'}
              onClick={() => setTheme('light')}
              className="flex items-center cursor-pointer bg-transparent border-0"
              style={{ paddingLeft: '10px', paddingRight: '10px', borderRadius: '8px' }}
              aria-label="Light theme"
              title="Light theme"
            >
              <Sun size={13} />
            </button>
          </div>
          {/* No bell. The shell once rendered a bell button with no handler, no
              accessible name and a hard-coded red dot — an "unread" bound to no
              read path. It returns WITH a notification channel, never before
              one (data honesty outranks the Stier README's "notification dot").
              Calendar, Settings and Log out left this bar in slice 2b (Tools
              rows; the account menu). */}
        </div>
      </header>

      {/* ── Main — .k-page padding (22px 24px 44px). The flex/gap half of
             .k-page is deliberately NOT applied: pages own their own internal
             spacing today, and adding a parent gap would double-space them.
             A viewportOwning page (the pipeline board) instead gets an
             unpadded, non-scrolling main it fills exactly — its .k-pipeline
             head/board carry their own padding and scrolling (§6.5). ── */}
      <main
        className={viewportOwning ? 'overflow-hidden' : 'overflow-y-auto'}
        style={{
          gridArea: 'main',
          background: surface.content,
          padding: viewportOwning ? 0 : '22px 24px 44px',
        }}
      >
        {children}
      </main>
    </div>
  )
}
